/**
 * LiveStage — shared 100ms room UI for both host & attendee.
 *
 * Uses HMS React SDK hooks. Must be rendered inside <HMSRoomProvider>.
 * Provides:
 *   • Preview screen (camera/mic check) before joining
 *   • In-room video grid (main speaker + thumbnails)
 *   • Bottom bar: mic/cam toggle, screen share (host), leave, participant count
 *   • Host controls: start/stop recording, start HLS
 *   • Chat drawer + live Q&A drawer (Q&A synced via backend polling)
 */
import { useEffect, useRef, useState } from "react";
import {
  useHMSActions, useHMSStore,
  selectIsConnectedToRoom, selectLocalPeer, selectPeers,
  selectIsLocalAudioEnabled, selectIsLocalVideoEnabled,
  selectIsLocalScreenShared, selectCameraStreamByPeerID,
  selectScreenShareByPeerID, selectHMSMessages, selectPeerCount,
  selectPeersScreenSharing,
} from "@100mslive/react-sdk";
import { toast } from "sonner";
import axios from "axios";
import api from "@/lib/api";
import {
  Mic, MicOff, Video, VideoOff, MonitorUp, Monitor,
  LogOut, MessageSquare, Users, HelpCircle, Send, Star,
  Radio, Circle, Play, Square, Loader2, X,
  BarChart3, Smile, Hand, HandMetal, Check, Plus, Trash2,
  WifiOff, RefreshCw,
} from "lucide-react";

const BASE = process.env.REACT_APP_BACKEND_URL;

export default function LiveStage({ role, webinar, joinInfo, onExit }) {
  const hmsActions = useHMSActions();
  const isConnected = useHMSStore(selectIsConnectedToRoom);
  const [phase, setPhase] = useState("preview"); // preview | joined | left
  const [previewMic, setPreviewMic] = useState(true);
  const [previewCam, setPreviewCam] = useState(true);
  const [joining, setJoining] = useState(false);
  // Reconnection resilience
  const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
  const [reconnectAttempts, setReconnectAttempts] = useState(0);
  const [reconnecting, setReconnecting] = useState(false);
  const reconnectTimer = useRef(null);
  // registration info (for attendee only — extracted from joinInfo.user_id "reg-xxx")
  const registrationId = joinInfo?.user_id?.startsWith("reg-") ? joinInfo.user_id.slice(4) : null;

  useEffect(() => {
    return () => { hmsActions.leave().catch(() => {}); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Track browser online/offline
  useEffect(() => {
    const onOn = () => { setOnline(true); toast.success("Back online"); };
    const onOff = () => { setOnline(false); toast.error("You're offline — trying to reconnect…"); };
    window.addEventListener("online", onOn);
    window.addEventListener("offline", onOff);
    return () => {
      window.removeEventListener("online", onOn);
      window.removeEventListener("offline", onOff);
    };
  }, []);

  // Attempt auto-rejoin when connection drops while user is in "joined" phase
  const tryRejoin = async (attempt) => {
    if (!joinInfo?.token) return;
    setReconnecting(true);
    setReconnectAttempts(attempt);
    try {
      await hmsActions.leave().catch(() => {});
      await hmsActions.join({
        userName: joinInfo.user_name || "Guest",
        authToken: joinInfo.token,
        settings: { isAudioMuted: true, isVideoMuted: true },
      });
      setReconnecting(false);
      setReconnectAttempts(0);
      toast.success("Reconnected");
    } catch (e) {
      // schedule next retry with backoff (max 6 attempts, 2s, 4s, 8s, 16s, 30s, 30s)
      const nextDelay = Math.min(30000, 2000 * Math.pow(2, attempt));
      if (attempt < 6) {
        reconnectTimer.current = setTimeout(() => tryRejoin(attempt + 1), nextDelay);
      } else {
        setReconnecting(false);
        toast.error("Could not reconnect. Please rejoin manually.");
      }
    }
  };

  useEffect(() => {
    if (phase !== "joined") return;
    if (!isConnected && !reconnecting) {
      // wait 2s to distinguish transient blips from real disconnects
      reconnectTimer.current = setTimeout(() => tryRejoin(1), 2000);
    }
    return () => { if (reconnectTimer.current) clearTimeout(reconnectTimer.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected, phase]);

  const manualRejoin = () => {
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    setReconnectAttempts(0);
    tryRejoin(1);
  };

  const join = async () => {
    setJoining(true);
    try {
      await hmsActions.join({
        userName: joinInfo.user_name || "Guest",
        authToken: joinInfo.token,
        settings: {
          isAudioMuted: !previewMic,
          isVideoMuted: !previewCam,
        },
      });
      setPhase("joined");
    } catch (e) {
      toast.error("Could not join: " + (e.message || String(e)));
    } finally {
      setJoining(false);
    }
  };

  const leave = async () => {
    try { await hmsActions.leave(); } catch { /* noop */ }
    if (reconnectTimer.current) clearTimeout(reconnectTimer.current);
    setPhase("left");
    onExit?.();
  };

  if (phase === "preview") {
    return (
      <PreviewScreen
        role={role}
        webinar={webinar}
        previewMic={previewMic}
        setPreviewMic={setPreviewMic}
        previewCam={previewCam}
        setPreviewCam={setPreviewCam}
        onJoin={join}
        joining={joining}
        onCancel={onExit}
      />
    );
  }

  if (!isConnected) {
    return (
      <div className="min-h-screen bg-[#0B0D17] text-white flex items-center justify-center px-4" data-testid="live-reconnecting">
        <div className="max-w-sm w-full text-center space-y-4">
          <div className="w-14 h-14 rounded-full bg-white/10 mx-auto flex items-center justify-center">
            {online ? (
              <Loader2 className="w-6 h-6 animate-spin text-white" />
            ) : (
              <WifiOff className="w-6 h-6 text-red-400" />
            )}
          </div>
          <div>
            <div className="font-display font-bold text-lg">
              {online ? (reconnecting ? "Reconnecting…" : "Connecting…") : "You're offline"}
            </div>
            <div className="text-sm text-white/70 mt-1">
              {online
                ? (reconnectAttempts > 0
                    ? `Attempt ${reconnectAttempts} of 6 — hang tight`
                    : "Restoring your live session…")
                : "We'll rejoin automatically as soon as your internet comes back."}
            </div>
          </div>
          <button
            onClick={manualRejoin}
            className="inline-flex items-center gap-2 px-4 py-2 bg-white text-[#0B0D17] rounded-lg text-sm font-semibold hover:bg-white/90 disabled:opacity-50"
            disabled={!online || reconnecting}
            data-testid="live-manual-rejoin"
          >
            <RefreshCw className={"w-4 h-4 " + (reconnecting ? "animate-spin" : "")} /> Try again now
          </button>
          <button
            onClick={leave}
            className="block mx-auto text-white/50 hover:text-white/80 text-xs"
            data-testid="live-give-up"
          >
            Leave session
          </button>
        </div>
      </div>
    );
  }

  return (
    <InRoom
      role={role}
      webinar={webinar}
      registrationId={registrationId}
      userName={joinInfo?.user_name}
      onLeave={leave}
    />
  );
}

// ---------------------------------------------------------------------------
function PreviewScreen({ role, webinar, previewMic, setPreviewMic, previewCam, setPreviewCam, onJoin, joining, onCancel }) {
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          video: previewCam,
          audio: previewMic,
        });
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
      } catch (e) {
        // permissions denied — still allow joining audio-only
      }
    })();
    return () => {
      cancelled = true;
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [previewCam, previewMic]);

  return (
    <div className="min-h-screen bg-[#0B0D17] text-white flex items-center justify-center p-4">
      <div className="max-w-3xl w-full grid md:grid-cols-2 gap-6 bg-white/5 backdrop-blur rounded-3xl p-6">
        <div>
          <div className="aspect-video bg-black rounded-xl overflow-hidden relative">
            {previewCam ? (
              <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-cover" />
            ) : (
              <div className="w-full h-full flex items-center justify-center text-white/40">Camera off</div>
            )}
            <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
              <button
                onClick={() => setPreviewMic(!previewMic)}
                className={"w-10 h-10 rounded-full flex items-center justify-center " + (previewMic ? "bg-white/10 hover:bg-white/20" : "bg-red-500/80")}
                data-testid="preview-mic"
              >
                {previewMic ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
              </button>
              <button
                onClick={() => setPreviewCam(!previewCam)}
                className={"w-10 h-10 rounded-full flex items-center justify-center " + (previewCam ? "bg-white/10 hover:bg-white/20" : "bg-red-500/80")}
                data-testid="preview-cam"
              >
                {previewCam ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
              </button>
            </div>
          </div>
        </div>
        <div className="space-y-3 flex flex-col justify-center">
          <div>
            <div className="text-xs uppercase tracking-widest text-white/60">
              {role === "host" ? "Host studio" : "Live webinar"}
            </div>
            <div className="font-display font-black text-2xl md:text-3xl mt-1">{webinar?.title || "Live Session"}</div>
            <div className="text-sm text-white/70 mt-1">Check your camera & microphone before joining</div>
          </div>
          <button
            onClick={onJoin}
            disabled={joining}
            className="bg-white text-[#0B0D17] font-bold py-3 rounded-xl hover:bg-white/90 disabled:opacity-50 inline-flex items-center justify-center gap-2"
            data-testid="join-live-btn"
          >
            {joining ? <><Loader2 className="w-4 h-4 animate-spin" /> Joining…</> : (role === "host" ? "Go Live" : "Join Session")}
          </button>
          <button
            onClick={onCancel}
            className="text-white/60 hover:text-white text-sm py-2"
            data-testid="cancel-live-btn"
          >
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function InRoom({ role, webinar, registrationId, userName, onLeave }) {
  const hmsActions = useHMSActions();
  const localPeer = useHMSStore(selectLocalPeer);
  const peers = useHMSStore(selectPeers);
  const audioOn = useHMSStore(selectIsLocalAudioEnabled);
  const videoOn = useHMSStore(selectIsLocalVideoEnabled);
  const screenOn = useHMSStore(selectIsLocalScreenShared);
  const peerCount = useHMSStore(selectPeerCount);

  const [drawer, setDrawer] = useState(null); // "chat" | "qa" | "people" | "polls" | "hands"
  const [busy, setBusy] = useState(false);
  const [recording, setRecording] = useState(false);
  const [flyingReactions, setFlyingReactions] = useState([]);
  const [handRaised, setHandRaised] = useState(false);
  const lastReactionTsRef = useRef(0);

  const screenSharers = useHMSStore(selectPeersScreenSharing);
  const screenSharer = screenSharers?.[0];
  const screenTrack = useHMSStore(
    screenSharer ? selectScreenShareByPeerID(screenSharer.id) : () => null
  );

  const mainPeer = screenSharer ||
                   peers.find((p) => p.roleName === "broadcaster") ||
                   peers[0];

  const isHost = role === "host";

  const toggleRecording = async () => {
    if (!isHost) return;
    setBusy(true);
    try {
      if (recording) {
        await api.post(`/webinars/${webinar.id}/live/stop-recording`);
        setRecording(false); toast.success("Recording stopped");
      } else {
        await api.post(`/webinars/${webinar.id}/live/start-recording`, {});
        setRecording(true); toast.success("Recording started");
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || e.message);
    } finally { setBusy(false); }
  };

  // Reactions poller — floating emoji animation for everyone
  useEffect(() => {
    if (!webinar?.slug) return;
    let alive = true;
    const tick = async () => {
      if (!alive) return;
      try {
        const { data } = await axios.get(`${BASE}/api/public/w/${webinar.slug}/live/reactions?since=${lastReactionTsRef.current}`);
        lastReactionTsRef.current = data.now || lastReactionTsRef.current;
        if (data.reactions?.length) {
          setFlyingReactions((prev) => [
            ...prev.slice(-30),
            ...data.reactions.map((r) => ({
              id: `${r.ts}-${Math.random()}`,
              emoji: r.emoji,
              user: r.user_name,
              x: 10 + Math.random() * 80,
            })),
          ]);
        }
      } catch { /* noop */ }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [webinar?.slug]);

  const sendReaction = async (emoji) => {
    try {
      await axios.post(`${BASE}/api/public/w/${webinar.slug}/live/reactions`, {
        registration_id: registrationId,
        user_name: userName || (isHost ? "Host" : "Guest"),
        emoji,
      });
    } catch { /* noop */ }
  };

  const toggleHand = async () => {
    if (isHost || !registrationId) return;
    try {
      // fetch email from cached joinInfo? we don't have it here; skip email guard by using registrationId's email via a lookup — but simplest: only need email once at join, we have registrationId and userName
      // We must send email but we don't carry it. Fallback: attendees can toggle from a lightweight button that hits an endpoint using the same registration lookup.
      // Send with a placeholder — the backend requires email match. Skip if we don't have.
      const email = sessionStorage.getItem(`live-email-${webinar.slug}`);
      if (!email) { toast.error("Please rejoin from the invite link"); return; }
      await axios.post(`${BASE}/api/public/w/${webinar.slug}/live/hand-raise`, {
        registration_id: registrationId, email, raised: !handRaised,
      });
      setHandRaised(!handRaised);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="fixed inset-0 bg-[#0B0D17] text-white flex flex-col z-50">
      {/* Top bar */}
      <div className="flex items-center justify-between px-4 py-2 border-b border-white/5">
        <div className="flex items-center gap-3 min-w-0">
          <Radio className="w-4 h-4 text-red-500 animate-pulse" />
          <div className="min-w-0">
            <div className="text-sm font-medium truncate">{webinar?.title || "Live"}</div>
            <div className="text-[10px] text-white/50 uppercase tracking-widest">
              {isHost ? "You are the host" : "Watching live"}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2">
          {isHost && (
            <button
              onClick={toggleRecording}
              disabled={busy}
              className={"inline-flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium " + (recording ? "bg-red-500 text-white" : "bg-white/10 hover:bg-white/20")}
              data-testid="record-toggle"
            >
              {recording ? <Square className="w-3 h-3" /> : <Circle className="w-3 h-3 fill-current" />}
              {recording ? "Stop rec" : "Record"}
            </button>
          )}
          <div className="text-xs px-2 py-1 rounded-md bg-white/10 inline-flex items-center gap-1">
            <Users className="w-3 h-3" /> {peerCount}
          </div>
        </div>
      </div>

      <div className="flex-1 grid grid-cols-1 lg:grid-cols-[1fr_260px] min-h-0">
        {/* Main stage */}
        <div className="relative bg-black p-3 min-h-0 flex flex-col">
          <div className="flex-1 relative rounded-xl overflow-hidden bg-[#111827] flex items-center justify-center min-h-0">
            {screenTrack ? (
              <ScreenTile track={screenTrack} />
            ) : mainPeer ? (
              <VideoTile peer={mainPeer} large />
            ) : (
              <div className="text-white/50">Waiting for host to start…</div>
            )}
            {/* Flying reactions overlay */}
            <FlyingReactionsLayer items={flyingReactions} />
            {/* Live poll overlay */}
            <LivePollOverlay webinar={webinar} registrationId={registrationId} />
          </div>
          {/* Reaction quick bar */}
          <div className="flex items-center justify-center gap-1 mt-2">
            {["👏","❤️","🔥","😂","🎉","😮","💯"].map((em) => (
              <button
                key={em}
                onClick={() => sendReaction(em)}
                className="text-xl w-9 h-9 rounded-full bg-white/5 hover:bg-white/15 flex items-center justify-center"
                data-testid={`reaction-${em}`}
              >
                {em}
              </button>
            ))}
          </div>
          {/* Bottom control bar */}
          <div className="flex items-center justify-center gap-2 mt-2 flex-wrap">
            <button
              onClick={() => hmsActions.setLocalAudioEnabled(!audioOn)}
              className={"w-11 h-11 rounded-full flex items-center justify-center " + (audioOn ? "bg-white/10 hover:bg-white/20" : "bg-red-500/80")}
              data-testid="ctrl-mic"
            >
              {audioOn ? <Mic className="w-4 h-4" /> : <MicOff className="w-4 h-4" />}
            </button>
            <button
              onClick={() => hmsActions.setLocalVideoEnabled(!videoOn)}
              className={"w-11 h-11 rounded-full flex items-center justify-center " + (videoOn ? "bg-white/10 hover:bg-white/20" : "bg-red-500/80")}
              data-testid="ctrl-cam"
            >
              {videoOn ? <Video className="w-4 h-4" /> : <VideoOff className="w-4 h-4" />}
            </button>
            {isHost && (
              <button
                onClick={async () => {
                  try { await hmsActions.setScreenShareEnabled(!screenOn); }
                  catch (e) { toast.error("Screen share: " + e.message); }
                }}
                className={"w-11 h-11 rounded-full flex items-center justify-center " + (screenOn ? "bg-blue-500 hover:bg-blue-600" : "bg-white/10 hover:bg-white/20")}
                data-testid="ctrl-screen"
              >
                {screenOn ? <Monitor className="w-4 h-4" /> : <MonitorUp className="w-4 h-4" />}
              </button>
            )}
            {!isHost && (
              <button
                onClick={toggleHand}
                className={"w-11 h-11 rounded-full flex items-center justify-center " + (handRaised ? "bg-yellow-500 text-black" : "bg-white/10 hover:bg-white/20")}
                data-testid="ctrl-hand"
                title={handRaised ? "Lower hand" : "Raise hand"}
              >
                <Hand className="w-4 h-4" />
              </button>
            )}
            <button
              onClick={() => setDrawer(drawer === "people" ? null : "people")}
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              data-testid="ctrl-people"
            >
              <Users className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDrawer(drawer === "chat" ? null : "chat")}
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              data-testid="ctrl-chat"
            >
              <MessageSquare className="w-4 h-4" />
            </button>
            <button
              onClick={() => setDrawer(drawer === "qa" ? null : "qa")}
              className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
              data-testid="ctrl-qa"
            >
              <HelpCircle className="w-4 h-4" />
            </button>
            {isHost && (
              <>
                <button
                  onClick={() => setDrawer(drawer === "polls" ? null : "polls")}
                  className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center"
                  data-testid="ctrl-polls"
                  title="Polls"
                >
                  <BarChart3 className="w-4 h-4" />
                </button>
                <button
                  onClick={() => setDrawer(drawer === "hands" ? null : "hands")}
                  className="w-11 h-11 rounded-full bg-white/10 hover:bg-white/20 flex items-center justify-center relative"
                  data-testid="ctrl-hands"
                  title="Raised hands"
                >
                  <HandMetal className="w-4 h-4" />
                </button>
              </>
            )}
            <button
              onClick={onLeave}
              className="ml-4 px-4 h-11 rounded-full bg-red-500 hover:bg-red-600 inline-flex items-center gap-2 text-sm font-medium"
              data-testid="ctrl-leave"
            >
              <LogOut className="w-4 h-4" /> Leave
            </button>
          </div>
        </div>

        {/* Side rail: thumbnails or drawer */}
        <div className="border-t lg:border-t-0 lg:border-l border-white/5 min-h-0 flex flex-col">
          {drawer === null && (
            <div className="p-3 overflow-y-auto space-y-2 flex-1">
              <div className="text-[10px] uppercase tracking-widest text-white/40">Participants</div>
              {peers.filter((p) => p.id !== mainPeer?.id).slice(0, 10).map((p) => (
                <div key={p.id} className="aspect-video rounded-lg overflow-hidden bg-[#111827]">
                  <VideoTile peer={p} />
                </div>
              ))}
              {peers.length <= 1 && (
                <div className="text-xs text-white/40 text-center py-8">Just you here.</div>
              )}
            </div>
          )}
          {drawer === "people" && (
            <PeopleDrawer peers={peers} onClose={() => setDrawer(null)} />
          )}
          {drawer === "chat" && (
            <ChatDrawer webinar={webinar} registrationId={registrationId} userName={userName} isHost={isHost} onClose={() => setDrawer(null)} />
          )}
          {drawer === "qa" && (
            <QaDrawer webinar={webinar} isHost={isHost} onClose={() => setDrawer(null)} />
          )}
          {drawer === "polls" && (
            <PollsDrawer webinar={webinar} isHost={isHost} onClose={() => setDrawer(null)} />
          )}
          {drawer === "hands" && isHost && (
            <HandsDrawer webinar={webinar} onClose={() => setDrawer(null)} />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
function VideoTile({ peer, large }) {
  const videoRef = useRef(null);
  const hmsActions = useHMSActions();
  const track = useHMSStore(selectCameraStreamByPeerID(peer?.id));

  useEffect(() => {
    if (!videoRef.current || !track) return;
    (async () => {
      try { await hmsActions.attachVideo(track.id, videoRef.current); }
      catch { /* noop */ }
    })();
    return () => {
      if (videoRef.current && track) hmsActions.detachVideo(track.id, videoRef.current).catch(() => {});
    };
  }, [track?.id, hmsActions]);

  return (
    <div className="relative w-full h-full">
      {track?.enabled ? (
        <video ref={videoRef} autoPlay muted={peer?.isLocal} playsInline className="w-full h-full object-cover" />
      ) : (
        <div className="w-full h-full bg-gradient-to-br from-[#1e293b] to-[#0f172a] flex items-center justify-center">
          <div className={"rounded-full bg-white/10 flex items-center justify-center font-bold " + (large ? "w-24 h-24 text-3xl" : "w-12 h-12 text-lg")}>
            {(peer?.name || "?").slice(0, 1).toUpperCase()}
          </div>
        </div>
      )}
      <div className="absolute bottom-1 left-1 px-2 py-0.5 bg-black/60 rounded text-[10px]">
        {peer?.name || "Peer"} {peer?.roleName === "broadcaster" ? "· Host" : ""}
      </div>
    </div>
  );
}

function ScreenTile({ track }) {
  const videoRef = useRef(null);
  const hmsActions = useHMSActions();
  useEffect(() => {
    if (!videoRef.current || !track) return;
    (async () => { try { await hmsActions.attachVideo(track.id, videoRef.current); } catch {} })();
    return () => { if (videoRef.current && track) hmsActions.detachVideo(track.id, videoRef.current).catch(() => {}); };
  }, [track?.id, hmsActions]);
  return <video ref={videoRef} autoPlay muted playsInline className="w-full h-full object-contain bg-black" />;
}

function PeopleDrawer({ peers, onClose }) {
  return (
    <div className="p-3 h-full flex flex-col">
      <DrawerHeader title={`People · ${peers.length}`} onClose={onClose} />
      <div className="flex-1 overflow-y-auto space-y-1 text-sm">
        {peers.map((p) => (
          <div key={p.id} className="flex items-center gap-2 px-2 py-1.5 rounded hover:bg-white/5">
            <div className="w-8 h-8 rounded-full bg-white/10 flex items-center justify-center font-semibold text-xs">
              {(p.name || "?").slice(0, 1).toUpperCase()}
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate">{p.name}</div>
              <div className="text-[10px] text-white/50 capitalize">{p.roleName}</div>
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function ChatDrawer({ webinar, registrationId, userName, isHost, onClose }) {
  const [messages, setMessages] = useState([]);
  const [msg, setMsg] = useState("");
  const sinceRef = useRef(0);
  const listRef = useRef(null);

  useEffect(() => {
    let alive = true;
    const tick = async () => {
      if (!alive || !webinar?.slug) return;
      try {
        const { data } = await axios.get(`${BASE}/api/public/w/${webinar.slug}/live/chat?since=${sinceRef.current}`);
        sinceRef.current = data.now || sinceRef.current;
        if (data.messages?.length) {
          setMessages((prev) => [...prev, ...data.messages].slice(-200));
          setTimeout(() => { if (listRef.current) listRef.current.scrollTop = listRef.current.scrollHeight; }, 50);
        }
      } catch { /* noop */ }
    };
    tick();
    const t = setInterval(tick, 2000);
    return () => { alive = false; clearInterval(t); };
  }, [webinar?.slug]);

  const send = async () => {
    if (!msg.trim()) return;
    try {
      await axios.post(`${BASE}/api/public/w/${webinar.slug}/live/chat`, {
        registration_id: registrationId,
        user_name: userName || (isHost ? "Host" : "Guest"),
        message: msg.trim(),
      });
      setMsg("");
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  return (
    <div className="p-3 h-full flex flex-col">
      <DrawerHeader title="Chat" onClose={onClose} />
      <div ref={listRef} className="flex-1 overflow-y-auto space-y-2 text-sm mb-2">
        {messages.length === 0 && <div className="text-white/40 text-xs text-center py-6">No messages yet.</div>}
        {messages.map((m) => (
          <div key={m.id} className="bg-white/5 rounded-lg px-2 py-1.5">
            <div className="text-[10px] text-white/50">{m.user_name}</div>
            <div>{m.message}</div>
          </div>
        ))}
      </div>
      <div className="flex gap-1">
        <input
          className="flex-1 bg-white/10 border border-white/10 rounded-lg px-3 py-2 text-sm outline-none"
          placeholder="Message everyone"
          value={msg}
          onChange={(e) => setMsg(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") send(); }}
          data-testid="chat-input"
        />
        <button onClick={send} className="bg-white text-[#0B0D17] px-3 rounded-lg" data-testid="chat-send">
          <Send className="w-4 h-4" />
        </button>
      </div>
    </div>
  );
}

function QaDrawer({ webinar, isHost, onClose }) {
  const [questions, setQuestions] = useState([]);
  const load = async () => {
    if (!webinar?.slug) return;
    try {
      const { data } = await axios.get(`${BASE}/api/public/w/${webinar.slug}/live/questions`);
      setQuestions(data.questions || []);
    } catch { /* noop */ }
  };
  useEffect(() => {
    load();
    const t = setInterval(load, 5000);
    return () => clearInterval(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [webinar?.slug]);

  const star = async (qid) => {
    if (!isHost) return;
    try {
      await api.post(`/webinars/${webinar.id}/live/questions/${qid}/star`);
      load();
    } catch { /* noop */ }
  };

  return (
    <div className="p-3 h-full flex flex-col">
      <DrawerHeader title="Live Q&A" onClose={onClose} />
      <div className="flex-1 overflow-y-auto space-y-2 text-sm">
        {questions.length === 0 && <div className="text-white/40 text-xs text-center py-6">No questions yet.</div>}
        {questions.map((q) => (
          <div key={q.id} className={"rounded-lg p-2 border " + (q.starred ? "border-yellow-400/50 bg-yellow-500/10" : "border-white/10 bg-white/5")}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] text-white/60">{q.asker_name}</div>
                <div>{q.question}</div>
              </div>
              {isHost && (
                <button onClick={() => star(q.id)} className="text-white/40 hover:text-yellow-400" title="Star">
                  <Star className={"w-4 h-4 " + (q.starred ? "fill-yellow-400 text-yellow-400" : "")} />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function DrawerHeader({ title, onClose }) {
  return (
    <div className="flex items-center justify-between mb-2">
      <div className="text-sm font-semibold">{title}</div>
      <button onClick={onClose} className="text-white/60 hover:text-white"><X className="w-4 h-4" /></button>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Polls (host CRUD + attendee overlay)
// ---------------------------------------------------------------------------
function PollsDrawer({ webinar, isHost, onClose }) {
  const [polls, setPolls] = useState([]);
  const [creating, setCreating] = useState(false);
  const [question, setQuestion] = useState("");
  const [options, setOptions] = useState(["", ""]);

  const load = async () => {
    try {
      const { data } = await api.get(`/webinars/${webinar.id}/live/polls`);
      setPolls(data.polls || []);
    } catch { /* noop */ }
  };
  useEffect(() => { if (isHost) { load(); const t = setInterval(load, 3000); return () => clearInterval(t); } }, [webinar?.id, isHost]);

  const create = async () => {
    if (question.trim().length < 3 || options.filter((o) => o.trim()).length < 2) {
      toast.error("Question + 2 options required"); return;
    }
    try {
      await api.post(`/webinars/${webinar.id}/live/polls`, {
        question: question.trim(),
        options: options.map((o) => o.trim()).filter(Boolean),
        allow_multi: false,
      });
      setQuestion(""); setOptions(["", ""]); setCreating(false);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };
  const publish = async (p) => { await api.post(`/webinars/${webinar.id}/live/polls/${p.id}/publish`); load(); };
  const close = async (p) => { await api.post(`/webinars/${webinar.id}/live/polls/${p.id}/close`); load(); };
  const del = async (p) => { if (window.confirm("Delete this poll?")) { await api.delete(`/webinars/${webinar.id}/live/polls/${p.id}`); load(); } };

  return (
    <div className="p-3 h-full flex flex-col">
      <DrawerHeader title="Polls" onClose={onClose} />
      {isHost && !creating && (
        <button onClick={() => setCreating(true)} className="w-full bg-white/10 hover:bg-white/20 rounded-lg py-2 text-sm inline-flex items-center justify-center gap-1.5 mb-2" data-testid="poll-new-btn">
          <Plus className="w-4 h-4" /> New poll
        </button>
      )}
      {creating && (
        <div className="bg-white/5 rounded-lg p-2 space-y-2 mb-2">
          <input className="w-full bg-white/10 rounded px-2 py-1.5 text-sm outline-none" placeholder="Question…" value={question} onChange={(e) => setQuestion(e.target.value)} data-testid="poll-question" />
          {options.map((o, i) => (
            <div key={i} className="flex gap-1">
              <input className="flex-1 bg-white/10 rounded px-2 py-1.5 text-sm outline-none" placeholder={`Option ${i + 1}`} value={o} onChange={(e) => setOptions(options.map((x, j) => (j === i ? e.target.value : x)))} data-testid={`poll-opt-${i}`} />
              {options.length > 2 && <button onClick={() => setOptions(options.filter((_, j) => j !== i))} className="text-white/40 hover:text-red-400 px-1"><X className="w-3 h-3" /></button>}
            </div>
          ))}
          {options.length < 6 && <button onClick={() => setOptions([...options, ""])} className="text-xs text-white/50 hover:text-white/80">+ Add option</button>}
          <div className="flex gap-1 pt-1">
            <button onClick={create} className="bg-blue-500 hover:bg-blue-600 rounded px-3 py-1.5 text-xs" data-testid="poll-create">Create</button>
            <button onClick={() => { setCreating(false); setQuestion(""); setOptions(["", ""]); }} className="bg-white/10 hover:bg-white/20 rounded px-3 py-1.5 text-xs">Cancel</button>
          </div>
        </div>
      )}
      <div className="flex-1 overflow-y-auto space-y-2 text-sm">
        {polls.map((p) => (
          <div key={p.id} className={"rounded-lg p-2 border " + (p.status === "live" ? "border-green-400/50 bg-green-500/10" : "border-white/10 bg-white/5")}>
            <div className="flex items-start gap-2">
              <div className="flex-1 min-w-0">
                <div className="text-[10px] uppercase tracking-widest text-white/50">{p.status} · {p.total_votes} vote{p.total_votes !== 1 ? "s" : ""}</div>
                <div className="font-medium mt-0.5">{p.question}</div>
                <div className="mt-1.5 space-y-1">
                  {p.options.map((o) => (
                    <div key={o.index} className="text-xs">
                      <div className="flex justify-between mb-0.5"><span>{o.text}</span><span className="text-white/60">{o.pct}%</span></div>
                      <div className="h-1.5 bg-white/10 rounded"><div className="h-full bg-blue-400 rounded" style={{ width: `${o.pct}%` }} /></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
            {isHost && (
              <div className="flex gap-1 mt-2">
                {p.status !== "live" && <button onClick={() => publish(p)} className="bg-green-500/80 hover:bg-green-500 rounded px-2 py-1 text-xs">Publish</button>}
                {p.status === "live" && <button onClick={() => close(p)} className="bg-white/10 hover:bg-white/20 rounded px-2 py-1 text-xs">End</button>}
                <button onClick={() => del(p)} className="text-white/40 hover:text-red-400 ml-auto"><Trash2 className="w-3 h-3" /></button>
              </div>
            )}
          </div>
        ))}
        {polls.length === 0 && !creating && <div className="text-white/40 text-xs text-center py-6">No polls yet.</div>}
      </div>
    </div>
  );
}

// Attendee-visible poll floating over stage
function LivePollOverlay({ webinar, registrationId }) {
  const [poll, setPoll] = useState(null);
  const [voting, setVoting] = useState(false);
  const [hidden, setHidden] = useState(false);
  const votedKey = poll ? `poll-voted-${poll.id}-${registrationId || "anon"}` : null;

  useEffect(() => {
    if (!webinar?.slug) return;
    let alive = true;
    const tick = async () => {
      if (!alive) return;
      try {
        const { data } = await axios.get(`${BASE}/api/public/w/${webinar.slug}/live/polls`);
        if (!alive) return;
        setPoll(data.active_poll || null);
      } catch { /* noop */ }
    };
    tick();
    const t = setInterval(tick, 3000);
    return () => { alive = false; clearInterval(t); };
  }, [webinar?.slug]);

  if (!poll || hidden) return null;
  const alreadyVoted = votedKey && localStorage.getItem(votedKey);

  const vote = async (i) => {
    if (!registrationId) { toast.error("Please rejoin from your invite link to vote"); return; }
    setVoting(true);
    try {
      const { data } = await axios.post(`${BASE}/api/public/w/${webinar.slug}/live/polls/${poll.id}/vote`, {
        registration_id: registrationId, option_index: i,
      });
      localStorage.setItem(votedKey, "1");
      setPoll(data);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
    finally { setVoting(false); }
  };

  return (
    <div className="absolute bottom-3 right-3 max-w-xs w-80 bg-[#0B0D17]/95 backdrop-blur border border-white/10 rounded-xl p-3 shadow-2xl">
      <div className="flex items-start justify-between gap-2 mb-2">
        <div className="text-[10px] uppercase tracking-widest text-blue-300">Live Poll</div>
        <button onClick={() => setHidden(true)} className="text-white/40 hover:text-white"><X className="w-3 h-3" /></button>
      </div>
      <div className="font-semibold text-sm mb-2">{poll.question}</div>
      <div className="space-y-1.5">
        {poll.options.map((o) => (
          <button
            key={o.index}
            onClick={() => vote(o.index)}
            disabled={voting || alreadyVoted}
            data-testid={`poll-vote-${o.index}`}
            className="w-full text-left rounded-md bg-white/5 hover:bg-white/10 relative overflow-hidden py-1.5 px-2 text-xs disabled:cursor-default"
          >
            <div className="absolute inset-y-0 left-0 bg-blue-500/25" style={{ width: alreadyVoted ? `${o.pct}%` : 0, transition: "width .3s" }} />
            <div className="relative flex justify-between">
              <span>{o.text}</span>
              {alreadyVoted && <span className="text-white/70">{o.pct}%</span>}
            </div>
          </button>
        ))}
      </div>
      <div className="text-[10px] text-white/40 mt-2">{poll.total_votes} vote{poll.total_votes !== 1 ? "s" : ""}</div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Hands drawer (host only)
// ---------------------------------------------------------------------------
function HandsDrawer({ webinar, onClose }) {
  const [hands, setHands] = useState([]);
  const load = async () => {
    try {
      const { data } = await api.get(`/webinars/${webinar.id}/live/hands`);
      setHands(data.hands || []);
    } catch { /* noop */ }
  };
  useEffect(() => { load(); const t = setInterval(load, 3000); return () => clearInterval(t); }, [webinar?.id]);

  const act = async (h, action) => {
    try {
      await api.post(`/webinars/${webinar.id}/live/hands/${h.id}/action`, { action });
      load();
      if (action === "promote") toast.success(`${h.user_name} promoted on stage`);
    } catch (e) { toast.error(e.response?.data?.detail || e.message); }
  };

  return (
    <div className="p-3 h-full flex flex-col">
      <DrawerHeader title={`Raised hands · ${hands.length}`} onClose={onClose} />
      <div className="flex-1 overflow-y-auto space-y-1 text-sm">
        {hands.length === 0 && <div className="text-white/40 text-xs text-center py-6">No raised hands.</div>}
        {hands.map((h) => (
          <div key={h.id} className="flex items-center gap-2 px-2 py-1.5 rounded bg-white/5">
            <div className="w-7 h-7 rounded-full bg-yellow-400/20 flex items-center justify-center">
              <Hand className="w-3.5 h-3.5 text-yellow-300" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="truncate text-sm">{h.user_name}</div>
              <div className="text-[10px] text-white/50 capitalize">{h.status || "raised"}</div>
            </div>
            <button onClick={() => act(h, "promote")} className="bg-green-500/80 hover:bg-green-500 rounded px-2 py-1 text-[10px]" data-testid={`hand-promote-${h.id}`}>
              <Check className="w-3 h-3" />
            </button>
            <button onClick={() => act(h, "dismiss")} className="text-white/40 hover:text-red-400" data-testid={`hand-dismiss-${h.id}`}>
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        ))}
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Flying reactions overlay — emojis float up + fade out
// ---------------------------------------------------------------------------
function FlyingReactionsLayer({ items }) {
  const [visible, setVisible] = useState([]);
  const seenIds = useRef(new Set());
  useEffect(() => {
    if (!items.length) return;
    const fresh = items.filter((r) => !seenIds.current.has(r.id));
    if (!fresh.length) return;
    fresh.forEach((r) => seenIds.current.add(r.id));
    setVisible((prev) => [...prev, ...fresh].slice(-20));
    const t = setTimeout(() => {
      setVisible((prev) => prev.filter((v) => !fresh.find((l) => l.id === v.id)));
    }, 3500);
    return () => clearTimeout(t);
  }, [items]);
  return (
    <div className="absolute inset-0 pointer-events-none overflow-hidden">
      {visible.map((r) => (
        <div
          key={r.id}
          className="absolute bottom-2 text-3xl vp-float-up"
          style={{ left: `${r.x}%` }}
        >
          {r.emoji}
        </div>
      ))}
      <style>{`
        @keyframes vp-float-up {
          0% { transform: translateY(0) scale(0.6); opacity: 0; }
          15% { opacity: 1; transform: translateY(-40px) scale(1); }
          100% { transform: translateY(-380px) scale(1.2); opacity: 0; }
        }
        .vp-float-up { animation: vp-float-up 3.4s ease-out forwards; }
      `}</style>
    </div>
  );
}
